import { throttle } from "lodash-es";
import type { DynamicDialogInstance } from "primevue/dynamicdialogoptions";
import { useDialog } from "primevue/usedialog";
import type { Ref } from "vue";
import { defineAsyncComponent, nextTick, ref, watch } from "vue";
import { useRoute } from "vue-router";

import { useEditManager, useEditStore } from "@/composables/edit-manager";
import {
    ArtefactOPFSFile,
    type ArtefactOPFSIdentity,
} from "@/lib/ArtefactOPFSFile";
import type { Artefact } from "@/lib/artefacts";
import {
    type EntityArgTypes,
    type SyncReturn,
    type SyncableEntityType,
    pagePerEntityAttribute,
    useSyncStore,
} from "@/stores/sync-store";

import { useOrganizationManager } from "./organization-manager";
import { registerFlush } from "./pending-flush";

// Lazy: ArtefactDraft pulls the DiffViewer/bpmn-js stack, and this composable
// is reached from eager Pinia stores — a static import would force all of it
// into the main chunk.
const ArtefactDraft = defineAsyncComponent(
    () => import("@/components/ArtefactDraft.vue")
);

export const FILE_SYNC_THROTTLE_MS = 500;

interface DraftOpts<T extends SyncableEntityType> {
    entityType: ArtefactOPFSIdentity<T>["entityType"];
    entityId: Ref<string | undefined>;
    attribute: ArtefactOPFSIdentity<T>["attribute"];
    artefact: Ref<Artefact | undefined>;
}

export const useDraftManager = <T extends SyncableEntityType>(
    opts: DraftOpts<T>
) => {
    const { artefact } = opts;
    const route = useRoute();

    const dialog = useDialog();
    const { resetFailedSyncs } = useSyncStore();

    const draft = ref<ArtefactOPFSFile<T>>();
    const { isEditing } = useEditManager();
    const { organization } = useOrganizationManager();
    const { startEditing } = useEditStore();

    const diffFrom = ref(new Blob([""]));
    const diffTo = ref(new Blob([""]));
    let openDialog: DynamicDialogInstance | undefined;

    const restoreDraft = async () => {
        if (draft.value && draft.value.handle) {
            const data = await (await draft.value.handle.getFile()).text();
            await artefact.value?.updateContent(data);
        }
        startEditing();

        if (!opts.entityId.value || !organization.value || !artefact.value) {
            return;
        }

        // When restoring, the artefact will not actually be deleted from the
        // filesystem as the actual save hasn't actually triggered yet, causing
        // the failed sync to persist. To hide it from the list, we pass the
        // identity of the artefact so that it can be temporarily excluded.
        //
        // It will only re-appear when:
        //  1. The user refreshes the page before the changes were saved to the backend.
        //  2. A subsequent save to the backend has failed.
        //
        //  So hiding it is not actually dangerous. It is just to avoid confusion
        //  for the user.
        await resetFailedSyncs({
            organizationId: organization.value!.identity,
            entityType: opts.entityType as SyncableEntityType,
            entityId: opts.entityId.value,
            attribute:
                opts.attribute as keyof EntityArgTypes[SyncableEntityType],
            mimetype: artefact.value.mimetype,
        });
    };

    const discardDraft = async () => {
        throttledSaveDraft.cancel();
        if (draft.value) {
            await draft.value.remove();
        } else if (
            opts.entityId.value &&
            organization.value &&
            artefact.value
        ) {
            (
                await ArtefactOPFSFile.get({
                    organizationId: organization.value.identity,
                    entityType: opts.entityType,
                    entityId: opts.entityId.value,
                    attribute: opts.attribute,
                    mimetype: artefact.value.mimetype,
                })
            )?.remove();
        }

        // Set to 'undefined', so the draft can be recreated if needed.
        draft.value = undefined;

        await resetFailedSyncs();
    };

    const throttledSaveDraft = throttle(async () => {
        if (!artefact.value?.blob) {
            return;
        }

        // Pin the entity context. `throttle.cancel()` does not abort an
        // in-flight invocation, so without these checks a navigation that
        // happens during the awaits below would let us land the new
        // entity's blob in the previous entity's OPFS file (or vice versa).
        const startEntityId = opts.entityId.value;
        const startOrgId = organization.value?.identity;
        const startMimetype = artefact.value.mimetype;

        if (!startEntityId || !startOrgId) {
            return;
        }

        const isStale = () =>
            opts.entityId.value !== startEntityId ||
            organization.value?.identity !== startOrgId ||
            !artefact.value ||
            artefact.value.mimetype !== startMimetype;

        // Drop the cached handle if it points at a different entity (e.g. the
        // reset watcher cleared it while the previous invocation was awaiting,
        // but the prior assignment still re-installed the old handle).
        if (draft.value && draft.value.info.entityId !== startEntityId) {
            draft.value = undefined;
        }

        if (!draft.value) {
            const file = await ArtefactOPFSFile.getOrCreate({
                organizationId: startOrgId,
                entityType: opts.entityType,
                entityId: startEntityId,
                attribute: opts.attribute,
                mimetype: startMimetype,
            });
            if (isStale()) {
                return;
            }
            draft.value = file;
        }

        if (draft.value && !isStale()) {
            await draft.value.write(artefact.value!.blob);
        }
    }, FILE_SYNC_THROTTLE_MS);

    // Register with the centralized flush registry so that the router.beforeEach
    // guard can flush this draft write before any navigation.
    registerFlush(() => throttledSaveDraft.flush());

    /**
     * Compare two blobs for equality.
     */
    const blobsEqual = async (blobA: Blob, blobB: Blob): Promise<boolean> => {
        if (blobA.size !== blobB.size) {
            return false;
        }

        const bufferA = await blobA.arrayBuffer();
        const bufferB = await blobB.arrayBuffer();

        async function hash(buffer: ArrayBuffer) {
            const digest = await crypto.subtle.digest("SHA-1", buffer);
            return new Uint8Array(digest).toString();
        }

        return (await hash(bufferA)) === (await hash(bufferB));
    };

    const checkAndRestoreDraft = async () => {
        if (!opts.entityId.value) {
            return;
        }

        if (!artefact.value?.blob) {
            return;
        }

        if (!organization.value) {
            return;
        }

        // No need to show the restore dialog when we're not on the page in which
        // the specific attribute is normally edited.
        if (
            pagePerEntityAttribute[opts.entityType][opts.attribute] !==
            route.name
        ) {
            return;
        }

        // Pin the identity we started with. If the user navigates to another
        // entity while we're awaiting OPFS calls below, artefact.value will be
        // replaced — continuing would compare or display blobs from a stale
        // entity.
        const startIdentity = artefact.value.identity;
        const isStale = () => artefact.value?.identity !== startIdentity;

        const fetchedDraft = await ArtefactOPFSFile.get({
            organizationId: organization.value.identity,
            entityType: opts.entityType,
            entityId: opts.entityId.value,
            attribute: opts.attribute,
            mimetype: artefact.value.mimetype,
        });

        if (isStale()) {
            return;
        }

        draft.value = fetchedDraft;

        if (!draft.value || !draft.value.handle) {
            return;
        }

        const data = await draft.value.handle.getFile();

        if (isStale()) {
            return;
        }

        // Stale/empty draft — clean it up rather than showing a restore dialog
        // with no content. This can happen when the OPFS file was created but
        // the pending write was abandoned (e.g. the throttle fired after
        // navigation) before any content was flushed into it.
        if (data.size === 0) {
            await draft.value.remove();
            draft.value = undefined;
            return;
        }

        // Artefact does not exist, or is exactly the same as stored
        if (!data || (await blobsEqual(data, artefact.value.blob))) {
            if (isStale()) {
                return;
            }
            draft.value.remove();
            await resetFailedSyncs();
            return;
        }

        if (isStale()) {
            return;
        }

        diffFrom.value = artefact.value.blob;
        diffTo.value = data;

        openDialog = dialog.open(ArtefactDraft, {
            props: {
                header: `Draft found for ${artefact.value.name}. Restore it?`,
                closable: false,
                modal: true,
                draggable: false,
                style: {
                    width: "80vw",
                },
            },
            data: {
                artefact: artefact.value,
                diffFrom: diffFrom.value,
                diffTo: diffTo.value,
            },
            emits: {
                onRestore: () => {
                    restoreDraft();
                },
                onDiscard: () => {
                    discardDraft();
                },
            },
            onClose: () => {
                openDialog = undefined;
            },
        });
    };

    // When the artefact swaps to a different identity (e.g. the user
    // navigated to a different entity of the same type) the cached
    // `draft.value` handle is for the previous entity. Writing through it
    // would land the new artefact's bytes in the previous entity's OPFS file.
    // Any restore dialog still open is also showing the previous entity's
    // diff and is no longer relevant — close it.
    // Registered before the blob watcher so the throttled write below — if it
    // does run for the new artefact — starts from a clean state.
    watch(
        () => artefact.value?.identity,
        () => {
            throttledSaveDraft.cancel();
            draft.value = undefined;
            openDialog?.close();
            openDialog = undefined;
        }
    );

    // Save the in-memory blob to OPFS whenever it changes while editing. The
    // OPFS draft answers "do we have unsynced bytes?", which is *not* the same
    // as Artefact.isModified — queueSave() optimistically calls
    // Artefact.persist() before the backend round-trip completes, which would
    // defeat an isModified gate. Instead we ignore the first blob notification
    // observed for each Artefact *instance*: that blob is the just-fetched
    // server state, so persisting it would either spawn a self-cleaning draft
    // (best case) or silently overwrite a still-relevant draft from an
    // earlier failed sync (worst case). User edits mutate the existing
    // instance and so fall through this guard; backend refreshes replace the
    // instance entirely (even when the identity is unchanged) and so are
    // correctly skipped.
    let lastSeenArtefact: Artefact | undefined;
    watch(
        () => artefact.value?.blob,
        () => {
            if (!artefact.value) {
                return;
            }

            if (artefact.value !== lastSeenArtefact) {
                lastSeenArtefact = artefact.value;
                return;
            }

            if (isEditing.value) {
                throttledSaveDraft();
            }
        }
    );

    watch(
        () => isEditing.value,
        async (newValue) => {
            // If we just started editing and the file is modified already (e.g.
            // because the user overwrites a file by uploading a new file) - save
            // the draft.
            if (newValue === true && artefact.value?.isModified) {
                await throttledSaveDraft();
            }
        }
    );

    // Also trigger on 'route' changes, as when you switch from the definition page
    // to the process page you'll keep the same identities around.
    watch(
        [() => route.name, () => artefact.value?.identity],
        () => {
            if (artefact.value?.identity) {
                nextTick(async () => {
                    await checkAndRestoreDraft();
                });
            }
        },
        { immediate: true }
    );

    return {
        discardDraft,
    };
};

export function discardDraftsAfterSync(
    entityId: Ref<string | undefined>,
    drafts: ReturnType<typeof useDraftManager>[],
    entityType: SyncableEntityType
) {
    const syncStore = useSyncStore();
    syncStore.$onAction(({ name, after }) => {
        if (name === "sync") {
            after(async (fullySyncedIdentities: SyncReturn) => {
                // When the entity is fully synced, discard the drafts.
                //
                // Note that when we switched to a different page while an artefact is still being synced, the draft
                // will effectively still be discarded, just not through the draft manager but through the sync store
                // directly. This will happen through the discardPromises.
                // If still editing, the draft doesn't have to be removed either.
                if (!entityId.value) {
                    return;
                }

                for (const identity of fullySyncedIdentities) {
                    if (
                        identity.entityType === entityType &&
                        identity.entityId === entityId.value
                    ) {
                        for (const draft of drafts) {
                            try {
                                await draft.discardDraft();
                            } catch (e) {
                                if (!(e instanceof DOMException)) {
                                    // DOMExecption occurs if draft was already removed.
                                    throw e;
                                }
                            }
                        }
                        break;
                    }
                }
            });
        }
    });
}
